import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Sparkles, ClipboardCheck, ArrowRight, Loader2, RefreshCw, ShieldAlert, CircleCheck } from 'lucide-react';
import { formatRelative } from '../lib/uiHelpers';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_META = {
  complete:        { label: 'On track',       cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: CircleCheck },
  in_progress:     { label: 'In progress',    cls: 'text-violet-700 bg-violet-50 border-violet-200',    icon: ClipboardCheck },
  needs_attention: { label: 'Needs review',   cls: 'text-amber-700 bg-amber-50 border-amber-200',       icon: Sparkles },
  at_risk:         { label: 'At risk',        cls: 'text-rose-700 bg-rose-50 border-rose-200',          icon: ShieldAlert },
};

/**
 * B4. 30-Day Tech-Stack Audit Wizard
 *
 * A four-week playbook auto-populated from the tenant's live data.
 * Each week has: title · narrative · metric · action buttons · a
 * per-action checkbox that persists back to /api/audit/30day/state.
 */
export default function ThirtyDayAuditWizard({ token, onNavigateTab }) {
  const [snapshot, setSnapshot] = useState(null);
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/audit/30day`, { headers });
      setSnapshot(res.data.snapshot);
      setState(res.data.state || {});
    } catch (e) {
      toast.error('Failed to load audit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const generate = async () => {
    setRegen(true);
    try {
      const res = await axios.post(`${BACKEND}/api/audit/30day/generate`, {}, { headers });
      setSnapshot(res.data.snapshot);
      toast.success('Audit refreshed with your latest data');
    } catch (e) {
      toast.error('Could not refresh audit');
    } finally {
      setRegen(false);
    }
  };

  const toggleAction = (weekIdx, actionIdx) => {
    const key = `w${weekIdx + 1}_a${actionIdx + 1}`;
    const next = { ...state, [key]: !state[key] };
    setState(next);
    axios.patch(`${BACKEND}/api/audit/30day/state`, next, { headers }).catch(() => {
      toast.error('Could not save progress');
      setState(state);
    });
  };

  const actionRouting = (target) => {
    // Bridge the audit's target strings to real tab navigation.
    const map = {
      stack_map: 'visualizer',
      tech_database: 'database',
      reports_renewal: 'reports',
      reports_coverage: 'reports',
      ai_mapper: 'ai',
      ai_pathfinder: 'ai',
      ask_stack: 'home',
    };
    const tab = map[target];
    if (tab && onNavigateTab) onNavigateTab(tab);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-muted-foreground text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your 30-day audit…
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center" data-testid="audit-empty">
        <div className="relative mb-5 mx-auto w-16 h-16">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-violet-100 to-fuchsia-100 blur-xl" />
          <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-violet-200 bg-white shadow-sm">
            <Sparkles className="h-7 w-7 text-violet-500" />
          </div>
        </div>
        <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>30-Day Tech-Stack Audit</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-lg mx-auto">
          A four-week playbook, auto-populated from your live data — map every system, trace every connection,
          rank gaps by dollar-risk, and fix the top three. Regenerate any time.
        </p>
        <Button className="mt-5" onClick={generate} disabled={regen} data-testid="audit-generate-btn">
          {regen ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating…</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate my audit</>}
        </Button>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="audit-wizard">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-violet-600">30-Day Playbook</div>
            <h1 className="mt-1 text-2xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              Tech-Stack Audit
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Last generated {formatRelative(snapshot.generated_at)} · fresh as of your last property + assignment update.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={generate} disabled={regen} data-testid="audit-regenerate-btn">
            {regen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Refresh
          </Button>
        </div>

        {/* Weeks */}
        <div className="space-y-4">
          {(snapshot.weeks || []).map((w, wi) => {
            const meta = STATUS_META[w.status] || STATUS_META.in_progress;
            const StatusIcon = meta.icon;
            return (
              <div key={wi} className="rounded-xl border bg-card overflow-hidden" data-testid={`audit-week-${wi + 1}`}>
                <div className="flex items-center justify-between gap-3 border-b p-4 bg-gradient-to-r from-violet-50/50 to-fuchsia-50/30 dark:from-violet-950/20 dark:to-fuchsia-950/10">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Week {wi + 1}</div>
                    <div className="text-base font-semibold mt-0.5" style={{ fontFamily: 'Space Grotesk' }}>
                      {w.title.split(' · ').slice(1).join(' · ') || w.title}
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${meta.cls}`}>
                    <StatusIcon className="h-3 w-3" />
                    {meta.label}
                  </div>
                </div>
                <div className="p-4 grid gap-4 sm:grid-cols-[auto,1fr]">
                  {/* Metric column */}
                  <div className="sm:pr-4 sm:border-r border-dashed">
                    <div className="text-3xl font-bold text-violet-700 tabular-nums" style={{ fontFamily: 'Space Grotesk' }} data-testid={`audit-week-${wi + 1}-metric`}>
                      {w.metric}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 max-w-[130px]">{w.metric_label}</div>
                  </div>
                  {/* Narrative + actions */}
                  <div>
                    <p className="text-sm text-foreground/85 leading-relaxed" data-testid={`audit-week-${wi + 1}-narrative`}>
                      {w.narrative}
                    </p>
                    <div className="mt-3 space-y-1.5">
                      {(w.actions || []).map((a, ai) => {
                        const key = `w${wi + 1}_a${ai + 1}`;
                        const done = !!state[key];
                        return (
                          <div key={ai} className="flex items-center gap-2 group" data-testid={`audit-week-${wi + 1}-action-${ai + 1}`}>
                            <button
                              onClick={() => toggleAction(wi, ai)}
                              aria-checked={done}
                              role="checkbox"
                              className={`shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-sm border transition-colors ${done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-muted-foreground/40 hover:border-violet-500'}`}
                              data-testid={`audit-toggle-${key}`}
                            >
                              {done && <CircleCheck className="h-3 w-3" />}
                            </button>
                            <span className={`flex-1 text-sm ${done ? 'line-through text-muted-foreground' : 'text-foreground/90'}`}>
                              {a.label}
                            </span>
                            {a.target && (
                              <button
                                onClick={() => actionRouting(a.target)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-violet-600 hover:text-violet-800 text-[11px] inline-flex items-center gap-0.5"
                                data-testid={`audit-open-${key}`}
                              >
                                Open <ArrowRight className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {w.actions?.[0]?.why && (
                      <p className="text-[11px] text-muted-foreground italic mt-3 pl-1 border-l-2 border-violet-200 pl-3">
                        {w.actions[0].why}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
}
